/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { PriceComponent } from '../../../themes/krannich/views/components/price.component';
import { ICoreContext, IImageSettings, Image, ITelemetry, IGeneric, IAny } from '@msdyn365-commerce/core';
import { AsyncResult, ProductListLine } from '@msdyn365-commerce/retail-proxy';
import { Customer, ProductPrice, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { Button, INodeProps } from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';

import { IOrderTemplateLinePropsExtended, IOrderTemplateLineViewModel } from '../krannich-c-order-template';
import OrderTemplateQuantity from './common/krannich-c-order-template-quantity';
import { isRequestPrice } from '../../../utils/helper';

export interface IOrderTemplateLineProps extends ProductListLine {
    orderTemplateLine: IOrderTemplateLineViewModel;
    productListLine: ProductListLine;
    price?: ProductPrice;
    context: ICoreContext<IGeneric<IAny>>;
    productUrl: string;
    imageSettings?: IImageSettings;
    telemetry: ITelemetry;
    moduleId: string;
    moduleTypeName: string;
    quantity: number;
    isQuantityDisabledForUpdate?: boolean;
    handlers: {
        onAddToBag(product: SimpleProduct, quantity: number, itemKey: string): void;
        onRemoveItem(line: IOrderTemplateLineViewModel): void | undefined;
        onChangeQuantity(orderTemplateLineId: string, quantity: number, props?: IOrderTemplateLineProps): void;
        onSelect(line: IOrderTemplateLineViewModel, isChecked: boolean): void;
    };
    resources: {
        selectOneRowText?: string;
        moreButtonText?: string;
        viewProductDetailsText?: string;
        productDimensionTypeColor: string;
        productDimensionTypeSize: string;
        productDimensionTypeStyle: string;
        originalPriceText: string;
        currentPriceText: string;
        freePriceText: string;
        inputQuantityAriaLabel: string;
        removeFromOrderTemplateLinesText: string;
        addToBagButtonText: string;
        deleteButtonText: string;
        incrementButtonAriaLabel?: string;
        decrementButtonAriaLabel?: string;
        editOrderTemplateLineQuantityError?: string;
    };
    index: number;
    isMobile: boolean;
    productQuantityView?: React.ReactNode;
    addToBagButton?: React.ReactNode;
    removeButton?: React.ReactNode;
    toggleDetailsDisplayed(props: IOrderTemplateLineProps): void;
    customer: AsyncResult<Customer> | undefined;
}

export interface IOrderTemplateLineViewProps {
    key?: string;
    productImage: React.ReactNode;
    productPrice?: React.ReactNode;
    addToBagButton?: React.ReactNode;
    removeButton?: React.ReactNode;
    productName?: string;
    productNumber?: React.ReactNode;
    productInfo?: React.ReactNode;
    productLabel?: React.ReactNode;
    productUnitOfMeasure?: React.ReactNode;
    productQuantity?: number;
    productQuantityView?: React.ReactNode;
    productListId?: string;
    selectLine?: React.ReactNode;
    popUp?: JSX.Element;
    isMobile?: boolean;
    isDiscontinued?: boolean;
    isRequestPrice?: boolean;
    totalPrice?: number;
}

interface IEventHandlerFactory {
    getAddToBag(): (event: React.MouseEvent<HTMLElement>) => void;
    getRemoveItem(): (event: React.MouseEvent<HTMLElement>) => void;
}

const OrderTemplateLineActions = (props: IOrderTemplateLineProps): IEventHandlerFactory => {
    const { orderTemplateLine, productListLine, quantity, handlers } = props;
    const { LineId } = productListLine;
    const { onAddToBag, onRemoveItem } = handlers;

    return {
        getAddToBag: () => (event: React.MouseEvent<HTMLElement>) => {
            LineId && orderTemplateLine.fullProduct && onAddToBag(orderTemplateLine.fullProduct.ProductDetails, quantity, LineId);
        },
        getRemoveItem: () => (event: React.MouseEvent<HTMLElement>) => LineId && onRemoveItem(props.orderTemplateLine)
    };
};

interface IPopupProps extends IOrderTemplateLineProps {
    addButtonClassName: string;
    removeButtonClassName: string;
    kitDiscontinuedFlagValue: boolean;
}

const PopUp = (props: IPopupProps): JSX.Element | null => {
    const { addToBagButtonText, removeFromOrderTemplateLinesText, moreButtonText, viewProductDetailsText } = props.resources;
    const [showMobile, setShowMobile] = useState(false);
    const componentClassName = 'ms-table';

    const getViewHandler = () => (event: React.MouseEvent<HTMLElement>) => {
        props.toggleDetailsDisplayed(props);
    };

    const toggle = React.useCallback(() => {
        setShowMobile(!showMobile);
    }, [showMobile]);

    return (
        <div className={`${componentClassName}__row-links-minified`}>
            <Button
                className={`${componentClassName}__row-links-toggle`}
                aria-label={moreButtonText}
                data-type={props.index}
                onClick={toggle}
            />
            {showMobile && (
                <div className={`${componentClassName}__row-links`}>
                    <Button
                        className='ms-table__row-links-view'
                        onClick={getViewHandler()}
                        title={viewProductDetailsText}
                        aria-label={viewProductDetailsText}
                    >
                        EDIT
                    </Button>
                    {getActionButton(
                        props.isMobile,
                        props.removeButtonClassName,
                        removeFromOrderTemplateLinesText,
                        OrderTemplateLineActions(props).getRemoveItem()
                    )}
                    {getActionButton(
                        props.isMobile,
                        props.addButtonClassName,
                        addToBagButtonText,
                        OrderTemplateLineActions(props).getAddToBag(),
                        props.kitDiscontinuedFlagValue ? true : false
                    )}
                </div>
            )}
        </div>
    );
};

const getActionButton = (
    isMobile: boolean,
    className: string,
    buttonText: string,
    clickHandler: (event: React.MouseEvent<HTMLElement>) => void,
    disabled?: boolean
): React.ReactNode => (
    <Button aria-label={buttonText} className={className} onClick={clickHandler} title={buttonText} disabled={disabled}>
        {isMobile ? buttonText : ''}
    </Button>
);

/**
 * On Select line function.
 * @param orderTemplateLine -Order template line.
 * @param input -Order template line props.
 * @returns Set on select order template.
 */
const onSelectLineHandler = (orderTemplateLine: IOrderTemplateLineViewModel, input: IOrderTemplateLineProps) => (
    event: React.ChangeEvent<HTMLInputElement>
) => {
    orderTemplateLine.isChecked = !orderTemplateLine.isChecked;

    input.handlers.onSelect(orderTemplateLine, event.target.checked);
};

export const OrderTemplateLineView = (input: IOrderTemplateLinePropsExtended): IOrderTemplateLineViewProps | null => {
    const {
        orderTemplateLine,
        productListLine,
        price,
        context,
        imageSettings,
        quantity,
        resources,
        isMobile,
        kitDiscontinuedFlagValue,
        customer
    } = input;
    const product = orderTemplateLine.fullProduct;
    const { LineId, UnitOfMeasure } = productListLine;
    const { removeFromOrderTemplateLinesText, addToBagButtonText, selectOneRowText } = resources;
    if (!product) {
        return null;
    }

    const { ItemId, Name, PrimaryImageUrl } = product.ProductDetails;
    const fallbackImage = getFallbackImageUrl(ItemId, context.actionContext.requestContext.apiSettings);
    const addButtonClassName = input.isMobile ? 'ms-table__row-links-add' : 'ms-order-template-table-line__product-add-button';
    const removeButtonClassName = input.isMobile ? 'ms-table__row-links-delete' : 'ms-order-template-table-line__product-remove-button';
    return {
        isMobile,
        selectLine: (
            <label className='checkbox-container'>
                <input
                    aria-label={selectOneRowText}
                    className={
                        kitDiscontinuedFlagValue || isRequestPrice(price!, customer?.result?.LineDiscountGroup!)
                            ? 'checkbox-input-disabled'
                            : 'checkbox-input'
                    }
                    type='checkbox'
                    disabled={kitDiscontinuedFlagValue || isRequestPrice(price!, customer?.result?.LineDiscountGroup!)}
                    checked={orderTemplateLine.isChecked}
                    aria-checked={orderTemplateLine.isChecked}
                    onChange={onSelectLineHandler(orderTemplateLine, input)}
                />
                <span className='checkmark' />
            </label>
        ),
        key: LineId!,
        productImage:
            PrimaryImageUrl &&
            Name &&
            _renderProductImage(
                'ms-order-template-table-line__product-image',
                context,
                product.ProductDetails.Name!,
                PrimaryImageUrl,
                fallbackImage,
                imageSettings
            ),
        productInfo: renderProductInfo(input),
        productPrice: price && _renderPrice(input),
        productNumber: product && <div className='ms-order-template-table-line__product-number'>{ItemId}</div>,
        productUnitOfMeasure: price ? (
            <div className='ms-order-template-table-line__product-unit-of-measure'>{UnitOfMeasure || price.UnitOfMeasure}</div>
        ) : (
            undefined
        ),
        productListId: LineId!,
        removeButton: getActionButton(
            input.isMobile,
            removeButtonClassName,
            removeFromOrderTemplateLinesText,
            OrderTemplateLineActions(input).getRemoveItem()
        ),
        productQuantityView:
            isRequestPrice(price!, customer?.result?.LineDiscountGroup!) || kitDiscontinuedFlagValue ? (
                <div>
                    <OrderTemplateQuantity currentCount={1} disabled={true} />
                </div>
            ) : (
                <div>{_generateQuantity(input, LineId!)}</div>
            ),
        productQuantity: quantity,
        addToBagButton: getActionButton(
            input.isMobile,
            addButtonClassName,
            addToBagButtonText,
            OrderTemplateLineActions(input).getAddToBag()
        ),
        popUp: (
            <PopUp
                {...input}
                addButtonClassName={addButtonClassName}
                removeButtonClassName={removeButtonClassName}
                kitDiscontinuedFlagValue={kitDiscontinuedFlagValue}
            />
        ),
        isDiscontinued: kitDiscontinuedFlagValue,
        isRequestPrice: isRequestPrice(price!, customer?.result?.LineDiscountGroup!),
        totalPrice: isRequestPrice(price!, customer?.result?.LineDiscountGroup!)
            ? 0
            : Number(quantity) * Number(price?.CustomerContextualPrice)
    };
};

export const OrderTemplateMobileLineView = (input: IOrderTemplateLinePropsExtended): IOrderTemplateLineViewProps | undefined => {
    const { orderTemplateLine, productListLine, price, context, imageSettings, customer, kitDiscontinuedFlagValue } = input;
    const product = orderTemplateLine.fullProduct;
    const { LineId, UnitOfMeasure } = productListLine;
    if (!product) {
        return;
    }
    const { ItemId, Name, PrimaryImageUrl } = product.ProductDetails;
    const fallbackImage = getFallbackImageUrl(ItemId, context.actionContext.requestContext.apiSettings);

    return {
        productImage:
            PrimaryImageUrl &&
            Name &&
            _renderProductImage('thumbnail', context, product.ProductDetails.Name!, PrimaryImageUrl, fallbackImage, imageSettings),
        productInfo: renderProductInfo(input),
        productPrice: price && _renderPrice(input),
        productNumber: ItemId,
        productUnitOfMeasure: UnitOfMeasure || price?.UnitOfMeasure,
        productQuantityView:
            isRequestPrice(price!, customer?.result?.LineDiscountGroup!) || kitDiscontinuedFlagValue ? (
                <div>
                    <OrderTemplateQuantity currentCount={1} disabled={true} />
                </div>
            ) : (
                <div>{_generateQuantity(input, LineId!)}</div>
            )
    };
};

export interface IOrderTemplateProductQuantityViewProps {
    ContainerProps: INodeProps;
    input: React.ReactNode;
}

/**
 * On Change function.
 * @param onChangeQuantity -On change quantity function.
 * @param itemKey -String.
 * @param props -Order template line props.
 * @returns Set change quantity.
 */
const onChangeHandler = (
    onChangeQuantity: (orderTemplateLineId: string, quantity: number, props?: IOrderTemplateLineProps | undefined) => void,
    itemKey: string,
    props: IOrderTemplateLineProps
) => (newValue: number) => {
    if (onChangeQuantity) {
        onChangeQuantity(itemKey, newValue, props);
    }
};

const _generateQuantity = (props: IOrderTemplateLineProps, itemKey: string): React.ReactNode => {
    const { handlers, resources, quantity, orderTemplateLine } = props;
    const { onChangeQuantity } = handlers;
    const maximum =
        orderTemplateLine.fullProduct?.ProductDetails.Behavior?.MaximumQuantity &&
        orderTemplateLine.fullProduct?.ProductDetails.Behavior?.MaximumQuantity > 0
            ? orderTemplateLine.fullProduct?.ProductDetails.Behavior?.MaximumQuantity
            : props.context?.app?.config?.maxQuantityForCartLineItem || 10;
    const minimum =
        orderTemplateLine.fullProduct?.ProductDetails.Behavior?.MinimumQuantity !== undefined &&
        orderTemplateLine.fullProduct?.ProductDetails.Behavior?.MinimumQuantity !== 0
            ? orderTemplateLine.fullProduct?.ProductDetails.Behavior?.MinimumQuantity
            : 1;
    return (
        <OrderTemplateQuantity
            currentCount={quantity}
            onChange={onChangeHandler(onChangeQuantity, itemKey, props)}
            min={minimum}
            max={maximum}
            inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
            incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
            decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
            disabled={props.isQuantityDisabledForUpdate}
        />
    );
};

export const _renderProductImage = (
    className: string,
    context: ICoreContext,
    heading: string,
    image?: string,
    fallbackImage?: string,
    imageSettings?: IImageSettings
): JSX.Element | null => {
    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=64&h=64&m=6', w: 0, h: 0 },
            lg: { q: 'w=64&h=64&m=6', w: 0, h: 0 },
            xl: { q: 'w=64&h=64&m=6', w: 0, h: 0 }
        },
        lazyload: true
    };

    if (image) {
        return (
            <Image
                requestContext={context.actionContext.requestContext}
                className={className}
                altText={heading}
                title={heading}
                src={image}
                fallBackSrc={fallbackImage}
                gridSettings={context.request.gridSettings!}
                imageSettings={imageSettings || defaultImageSettings}
                loadFailureBehavior='empty'
                tabIndex={0}
            />
        );
    }
    return null;
};

const _renderPrice = (props: IOrderTemplateLineProps): JSX.Element | null => {
    const { price, context, moduleId, moduleTypeName, resources, customer } = props;
    if (isRequestPrice(price!, customer?.result?.LineDiscountGroup!)) {
        return (
            <div className='no-price-wrapper'>
                <span className='label'>Request Price</span>
                <div className='tooltip-wrapper'>
                    <span className='tooltip-icon' />
                    <span className='tooltip-text tooltip-left'>
                        {
                            'You cannot add this product to cart. Please contact your local Krannich Solar office or sales representative for current price and availability'
                        }
                    </span>
                </div>
            </div>
        );
    } else if (price) {
        return (
            <PriceComponent
                id={moduleId}
                typeName={moduleTypeName}
                data={{ price }}
                className='ms-order-template-table-line__product-price'
                freePriceText={resources.freePriceText}
                originalPriceText={resources.originalPriceText}
                currentPriceText={resources.currentPriceText}
                context={context}
                isIncExPriceFormatEnabled={true}
            />
        );
    }
    return null;
};

// todo: need to remove after validation
// const _renderProductDimensions = (props: IOrderTemplateLineProps): (string | undefined)[] => {
//     const { orderTemplateLine } = props;

//     if (!orderTemplateLine.fullProduct || !orderTemplateLine.fullProduct.ProductDetails.Dimensions) {
//         return [];
//     }

//     return orderTemplateLine.fullProduct.ProductDetails.Dimensions.map(dimension => {
//         if (!dimension.DimensionValue || !dimension.DimensionValue.Value) {
//             return undefined;
//         }

//         return dimension.DimensionValue.Value;
//     });
// };

export const renderProductInfo = (props: IOrderTemplateLinePropsExtended): React.ReactNode | undefined => {
    const { orderTemplateLine, productUrl, kitDiscontinuedFlagValue } = props;
    const name = orderTemplateLine.fullProduct?.ProductDetails.Name;

    /*const dimensions = _renderProductDimensions(props);
    if (!orderTemplateLine.fullProduct) {
        return [];
    }*/

    const productName = productUrl ? (
        <a className='ms-order-template-table-line__product-info-name' href={productUrl}>
            {name}
        </a>
    ) : (
        <div className='ms-order-template-table-line__product-info-name'>{name}</div>
    );

    //const dimensionsLine: string = dimensions.join(', ');

    return (
        <div className='ms-order-template-table-line__product-info'>
            <div className='ms-order-template-table-line__product-info-name'>{productName}</div>
            {kitDiscontinuedFlagValue ? <div className='discontinued-tag'>*Discontinued*</div> : null}
            {/*<div className='ms-order-template-table-line__product-info-dimension'>{dimensionsLine}</div>*/}
        </div>
    );
};
